/**
 * Zone Hosts Tab Component
 *
 * Displays a table of hosts for the current zone with expandable rows
 * showing instances running on each host.
 * Supports linking hosts to Warehouse equipment.
 */

import { useState, useMemo, Fragment } from 'react'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { getZoneCapacity } from '../../../services/capacity'
import {
    getZoneMappings,
    deleteMapping,
} from '../../../services/equipment-mapping'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
    ChevronRight,
    ChevronDown,
    Server,
    MonitorDot,
    Link2,
    Unlink,
    Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import MapEquipmentModal from '../MapEquipmentModal'
import type { HostCapacity, InstanceCapacity } from '../../../types/capacity'
import type { EquipmentZoneMapping } from '../../../types/mapping'

/** Get usage color class */
const getUsageClass = (pct: number): string => {
    if (pct >= 85) return 'text-red-600 font-semibold'
    if (pct >= 70) return 'text-amber-600'
    return 'text-green-600'
}

/** Get status badge props */
const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
        case 'connected':
        case 'online':
            return {
                variant: 'default' as const,
                className: 'bg-green-100 text-green-700 border-green-200',
                label: 'Connected',
            }
        case 'disconnected':
        case 'offline':
            return {
                variant: 'secondary' as const,
                className: 'bg-gray-100 text-gray-700 border-gray-200',
                label: 'Disconnected',
            }
        case 'maintenance':
            return {
                variant: 'outline' as const,
                className: 'bg-amber-100 text-amber-700 border-amber-200',
                label: 'Maintenance',
            }
        case 'error':
            return {
                variant: 'destructive' as const,
                className: 'bg-red-100 text-red-700 border-red-200',
                label: 'Error',
            }
        default:
            return {
                variant: 'outline' as const,
                className: '',
                label: status || 'Unknown',
            }
    }
}

/** Get instance status badge */
const getInstanceStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
        case 'running':
            return {
                className: 'bg-green-100 text-green-700',
                label: 'Running',
            }
        case 'stopped':
            return { className: 'bg-gray-100 text-gray-600', label: 'Stopped' }
        case 'suspended':
            return {
                className: 'bg-amber-100 text-amber-700',
                label: 'Suspended',
            }
        case 'starting':
        case 'stopping':
            return {
                className: 'bg-blue-100 text-blue-700',
                label: status.charAt(0).toUpperCase() + status.slice(1),
            }
        default:
            return {
                className: 'bg-gray-100 text-gray-600',
                label: status || 'Unknown',
            }
    }
}

/** Host row with expandable instances */
interface HostRowProps {
    host: HostCapacity
    instances: InstanceCapacity[]
    isExpanded: boolean
    onToggle: () => void
    mapping?: EquipmentZoneMapping
    onLinkClick: (host: HostCapacity) => void
    onUnlinkClick: (mapping: EquipmentZoneMapping) => void
    isUnlinking: boolean
}

const HostRow = ({
    host,
    instances,
    isExpanded,
    onToggle,
    mapping,
    onLinkClick,
    onUnlinkClick,
    isUnlinking,
}: HostRowProps) => {
    const statusBadge = getStatusBadge(host.status)
    const memoryGB = (host.memory_mb / 1024).toFixed(0)
    const hasInstances = instances.length > 0

    return (
        <Fragment>
            <TableRow
                className={cn(
                    hasInstances && 'cursor-pointer hover:bg-muted/50',
                    isExpanded && 'border-b-0 bg-muted/30'
                )}
                onClick={hasInstances ? onToggle : undefined}
            >
                <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                        {hasInstances ? (
                            isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                            ) : (
                                <ChevronRight className="h-4 w-4" />
                            )
                        ) : (
                            <div className="w-4" />
                        )}
                        <Server className="h-4 w-4 text-muted-foreground" />
                        {host.name}
                    </div>
                </TableCell>
                <TableCell className="text-right">{host.cpu_cores}</TableCell>
                <TableCell className="text-right">{memoryGB} GB</TableCell>
                <TableCell className="text-right">{host.vm_count}</TableCell>
                <TableCell
                    className={`text-right ${getUsageClass(host.cpu_usage_pct ?? 0)}`}
                >
                    {host.cpu_usage_pct?.toFixed(1) ?? '-'}%
                </TableCell>
                <TableCell
                    className={`text-right ${getUsageClass(host.memory_usage_pct ?? 0)}`}
                >
                    {host.memory_usage_pct?.toFixed(1) ?? '-'}%
                </TableCell>
                <TableCell className="text-center">
                    <Badge
                        variant={statusBadge.variant}
                        className={statusBadge.className}
                    >
                        {statusBadge.label}
                    </Badge>
                </TableCell>
                <TableCell
                    className="text-center"
                    onClick={(e) => e.stopPropagation()}
                >
                    {mapping ? (
                        <div className="flex items-center justify-center gap-2">
                            <Badge
                                variant="outline"
                                className="bg-green-50 text-green-700 border-green-200"
                            >
                                {mapping.item?.name || 'Linked'}
                            </Badge>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => onUnlinkClick(mapping)}
                                disabled={isUnlinking}
                            >
                                {isUnlinking ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                    <Unlink className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                                )}
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7"
                            onClick={() => onLinkClick(host)}
                        >
                            <Link2 className="h-3 w-3 mr-1" />
                            Link
                        </Button>
                    )}
                </TableCell>
            </TableRow>
            {isExpanded && (
                <TableRow className="bg-muted/20 hover:bg-muted/20">
                    <TableCell colSpan={8} className="p-0">
                        <div className="pl-12 pr-4 py-3">
                            <div className="text-sm font-medium text-muted-foreground mb-2">
                                Instances ({instances.length})
                            </div>
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                        <TableHead className="py-2">
                                            Name
                                        </TableHead>
                                        <TableHead className="py-2 text-right">
                                            CPU
                                        </TableHead>
                                        <TableHead className="py-2 text-right">
                                            Memory
                                        </TableHead>
                                        <TableHead className="py-2 text-right">
                                            Disk
                                        </TableHead>
                                        <TableHead className="py-2 text-center">
                                            Status
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {instances.map((instance) => {
                                        const instStatus =
                                            getInstanceStatusBadge(
                                                instance.status
                                            )
                                        return (
                                            <TableRow
                                                key={instance.uid}
                                                className="hover:bg-muted/50"
                                            >
                                                <TableCell className="py-2">
                                                    <div className="flex items-center gap-2">
                                                        <MonitorDot className="h-4 w-4 text-muted-foreground" />
                                                        {instance.name}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-2 text-right">
                                                    {instance.spec?.cpu ?? '-'}{' '}
                                                    vCPU
                                                </TableCell>
                                                <TableCell className="py-2 text-right">
                                                    {instance.spec?.memory_mb
                                                        ? `${(instance.spec.memory_mb / 1024).toFixed(1)} GB`
                                                        : '-'}
                                                </TableCell>
                                                <TableCell className="py-2 text-right">
                                                    {instance.spec?.disk_gb
                                                        ? `${instance.spec.disk_gb} GB`
                                                        : '-'}
                                                </TableCell>
                                                <TableCell className="py-2 text-center">
                                                    <Badge
                                                        variant="outline"
                                                        className={
                                                            instStatus.className
                                                        }
                                                    >
                                                        {instStatus.label}
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </TableCell>
                </TableRow>
            )}
        </Fragment>
    )
}

/** Host info for linking modal */
interface HostForLinking {
    id: string
    name: string
    zoneId: string
    zoneName: string
    providerType: string // Category: compute, cdn, storage
    providerCode: string // Animal code: panda, bear
    providerName?: string
}

const ZoneHosts = () => {
    const { zoneId } = useParams<{ zoneId: string }>()
    const queryClient = useQueryClient()
    const [expandedHosts, setExpandedHosts] = useState<Set<string>>(new Set())
    const [selectedHost, setSelectedHost] = useState<HostForLinking | null>(
        null
    )
    const [unlinkingId, setUnlinkingId] = useState<string | null>(null)

    // Fetch zone capacity which includes both hosts and instances
    const { data: zoneCapacity, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CAPACITY_ZONE, zoneId],
        queryFn: () => getZoneCapacity(zoneId!),
        enabled: !!zoneId,
    })

    // Fetch equipment-host mappings for this zone
    const { data: mappings } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_ZONE_MAPPINGS, zoneId],
        queryFn: () => getZoneMappings(zoneId!),
        enabled: !!zoneId,
    })

    // Create a map of host_id -> mapping for quick lookup
    const mappingsByHostId = useMemo(() => {
        const map = new Map<string, EquipmentZoneMapping>()
        if (mappings) {
            for (const mapping of mappings) {
                if (mapping.is_active) {
                    map.set(mapping.host_id, mapping)
                }
            }
        }
        return map
    }, [mappings])

    // Delete mapping mutation
    const deleteMappingMutation = useMutationWithHandler(
        {
            mutationFn: (id: string) => deleteMapping(id),
            onSuccess: () => {
                toast.success('Equipment unlinked successfully')
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.CAPACITY_ZONE, zoneId],
                })
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.WAREHOUSE_ZONE_MAPPINGS, zoneId],
                })
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.WAREHOUSE_UNMAPPED_EQUIPMENT],
                })
                setUnlinkingId(null)
            },
            onError: () => {
                setUnlinkingId(null)
            },
        },
        { showErrorToast: true }
    )

    // Group instances by host_id
    const instancesByHost = useMemo(() => {
        const grouped = new Map<string, InstanceCapacity[]>()
        if (zoneCapacity?.instances) {
            for (const instance of zoneCapacity.instances) {
                const hostId = instance.host_id || 'unassigned'
                if (!grouped.has(hostId)) {
                    grouped.set(hostId, [])
                }
                grouped.get(hostId)!.push(instance)
            }
        }
        return grouped
    }, [zoneCapacity?.instances])

    const hosts = zoneCapacity?.compute?.hosts || []

    const toggleHost = (hostId: string) => {
        setExpandedHosts((prev) => {
            const next = new Set(prev)
            if (next.has(hostId)) {
                next.delete(hostId)
            } else {
                next.add(hostId)
            }
            return next
        })
    }

    const handleLinkClick = (host: HostCapacity) => {
        setSelectedHost({
            id: host.id,
            name: host.name,
            zoneId: zoneId!,
            zoneName: zoneCapacity?.zone_name || '',
            providerType: zoneCapacity?.provider_type || 'compute', // Category: compute, cdn, storage
            providerCode: zoneCapacity?.provider_code || 'bear', // Animal code: panda, bear
            providerName: zoneCapacity?.provider_name || '',
        })
    }

    const handleUnlinkClick = (mapping: EquipmentZoneMapping) => {
        setUnlinkingId(mapping.id)
        deleteMappingMutation.mutate(mapping.id)
    }

    const handleModalClose = () => {
        setSelectedHost(null)
    }

    if (isLoading) {
        return (
            <Card className="mt-2">
                <CardHeader>
                    <CardTitle>Hosts</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-12 w-full" />
                        ))}
                    </div>
                </CardContent>
            </Card>
        )
    }

    if (hosts.length === 0) {
        return (
            <Card className="mt-2">
                <CardHeader>
                    <CardTitle>Hosts</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                        No hosts found in this zone
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card className="mt-2">
            <CardHeader>
                <CardTitle>Hosts ({hosts.length})</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Host Name</TableHead>
                                <TableHead className="text-right">
                                    CPU Cores
                                </TableHead>
                                <TableHead className="text-right">
                                    Memory
                                </TableHead>
                                <TableHead className="text-right">
                                    VMs
                                </TableHead>
                                <TableHead className="text-right">
                                    CPU Usage
                                </TableHead>
                                <TableHead className="text-right">
                                    Memory Usage
                                </TableHead>
                                <TableHead className="text-center">
                                    Status
                                </TableHead>
                                <TableHead className="text-center">
                                    Equipment
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {hosts.map((host) => (
                                <HostRow
                                    key={host.id}
                                    host={host}
                                    instances={
                                        instancesByHost.get(host.id) || []
                                    }
                                    isExpanded={expandedHosts.has(host.id)}
                                    onToggle={() => toggleHost(host.id)}
                                    mapping={mappingsByHostId.get(host.id)}
                                    onLinkClick={handleLinkClick}
                                    onUnlinkClick={handleUnlinkClick}
                                    isUnlinking={
                                        unlinkingId ===
                                        mappingsByHostId.get(host.id)?.id
                                    }
                                />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>

            {/* Map Equipment Modal */}
            {selectedHost && (
                <MapEquipmentModal
                    open={!!selectedHost}
                    onClose={handleModalClose}
                    host={selectedHost}
                />
            )}
        </Card>
    )
}

export default ZoneHosts
